import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// ─── Auth Lifecycle State Machine ────────────────────────────────────────────
// Replaces the two independent booleans (sessionStatus, isSessionRestoring)
// with a single, explicit lifecycle enum that guards can switch on.

export type AuthLifecycle =
  | 'RESTORING'        // Token found in storage, verifying with backend
  | 'AUTHENTICATED'    // Backend confirmed valid session
  | 'LOGGING_OUT'      // User/system initiated logout, cleanup in progress
  | 'UNAUTHENTICATED'  // No session (initial, post-logout, or expired)
  ;

/**
 * Legal state transitions. The setAuthLifecycle reducer enforces these.
 *
 *   UNAUTHENTICATED → RESTORING      (app boot with persisted token)
 *   RESTORING       → AUTHENTICATED  (backend confirms token)
 *   RESTORING       → UNAUTHENTICATED(token invalid/expired/no user)
 *   AUTHENTICATED   → LOGGING_OUT    (logout initiated)
 *   LOGGING_OUT     → UNAUTHENTICATED(cleanup complete)
 *
 * Illegal (blocked):
 *   LOGGING_OUT     → AUTHENTICATED  (prevents restore overriding logout)
 *   LOGGING_OUT     → RESTORING      (no restore during logout)
 *   UNAUTHENTICATED → AUTHENTICATED  (must go through login, which uses setAuthenticatedUser)
 */
const LEGAL_TRANSITIONS: Record<AuthLifecycle, AuthLifecycle[]> = {
  RESTORING:       ['AUTHENTICATED', 'UNAUTHENTICATED'],
  AUTHENTICATED:   ['LOGGING_OUT'],
  LOGGING_OUT:     ['UNAUTHENTICATED'],
  UNAUTHENTICATED: ['RESTORING'],
};

// ─── User Interface ──────────────────────────────────────────────────────────

interface User {
  id?: string;
  token?: string;
  fcmToken?: string;
  signupData?: any[];
  authStatus?: boolean;
  authLifecycle: AuthLifecycle;
  permissions?: string[]; // V2 Contract: Array of permission IDs
  deptPermissions?: any;
  sceneSetupPermission?: any;
  aiAssistantPermission?: any;
  allowRefferedCandidates?: any;
  departmentCreatePermission?: any;
  loggedUser?: any;
  company?: any;
  flowStep?: any;
  calendlyData?: any;
  scoreActionData?: any;
  addBot?: any;
  profileImage?: string;
  // RBAC V2: Role-based authority flags
  isSuperAdmin?: boolean;    // Role-based authority for UI gating
  superAdminCount?: number;  // For "last Super Admin" warning in UI
  onboarding?: {
    welcomeCompleted?: boolean;
    candidate?: Record<string, boolean> | Map<string, boolean>;
    company?: Record<string, boolean> | Map<string, boolean>;
  };
  // RBAC V3: Department-scoped permissions
  // CRITICAL SEMANTICS:
  //   null  → NO access (secure-by-default)
  //   string[] → scoped access to specific department IDs
  //   Super Admin bypass via isSuperAdmin flag, NOT via departmentScope
  departmentScope?: string[] | null;
  // Legacy / additional fields that come from API responses
  _id?: string;
  userName?: string;
  emailUsername?: string;
  userType?: string;
}

interface UserState {
  user: User;
}

const initialState: UserState = {
  user: {
    id: "",
    token: "",
    fcmToken: "",
    signupData: [],
    authStatus: false,
    authLifecycle: 'UNAUTHENTICATED',
    permissions: [], // V2 Contract: Array of strings
    deptPermissions: {},
    sceneSetupPermission: {},
    aiAssistantPermission: {},
    allowRefferedCandidates: {},
    departmentCreatePermission: {},
    loggedUser: {},
    flowStep: {},
    calendlyData: {},
    scoreActionData: {},
    addBot: {},
    // V3: Department scope - null = no access (secure-by-default)
    departmentScope: null,
  },
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    // ─── New Lifecycle Reducers ────────────────────────────────────────────

    /**
     * Transition the auth lifecycle state.
     * Enforces legal transitions — illegal transitions are blocked with a dev warning.
     */
    setAuthLifecycle: (state, action: PayloadAction<AuthLifecycle>) => {
      const current = state.user.authLifecycle;
      const next = action.payload;

      if (current === next) return;

      if (!LEGAL_TRANSITIONS[current]?.includes(next)) {
        if (process.env.NODE_ENV === 'development') {
          console.error(
            `[userSlice] Illegal auth transition blocked: ${current} → ${next}`,
          );
        }
        return; // Block illegal transition
      }

      state.user.authLifecycle = next;
    },

    /**
     * Set the fully authenticated user state.
     * This is the ONLY way to transition to AUTHENTICATED — it bypasses
     * setAuthLifecycle because login is a full state replacement, not a
     * lifecycle transition.
     *
     * Used by: login flows, session restore success path.
     */
    setAuthenticatedUser: (state, action: PayloadAction<User>) => {
      if (!action.payload || typeof action.payload !== 'object') {
        if (process.env.NODE_ENV === 'development') {
          console.error('[userSlice] setAuthenticatedUser called with invalid payload — blocked');
        }
        return;
      }
      state.user = { ...action.payload, authLifecycle: 'AUTHENTICATED' };
    },

    /**
     * Clear all user state for logout/account deletion.
     * Sets lifecycle to UNAUTHENTICATED explicitly — no ambiguous intermediate states.
     */
    clearAuthenticatedUser: (state) => {
      state.user = { ...initialState.user, authLifecycle: 'UNAUTHENTICATED' };
    },

    // ─── Existing Reducers (preserved for non-auth functionality) ──────────

    addUser: (state, action: PayloadAction<User>) => {
      if (!action.payload || typeof action.payload !== 'object') {
        if (process.env.NODE_ENV === 'development') {
          console.error(
            '[userSlice] addUser called with invalid payload — blocked. ' +
            'Use setAuthenticatedUser for login or clearAuthenticatedUser for logout.',
          );
        }
        return; // Don't corrupt state
      }
      state.user = action.payload;
    },
    addCompany: (state, action: PayloadAction<{ signupData: any[] }>) => {
      state.user.signupData = action.payload.signupData;
    },
    updateAuthStatus: (state, action: PayloadAction<boolean>) => {
      state.user.authStatus = action.payload;
    },
    userPermission: (state, action: PayloadAction<any>) => {
      state.user.permissions = action.payload;
    },
    departmentPermission: (state, action: PayloadAction<any>) => {
      state.user.deptPermissions = action.payload;
    },
    sceneSetupPermission: (state, action: PayloadAction<any>) => {
      state.user.sceneSetupPermission = action.payload;
    },
    aiAssistantPermission: (state, action: PayloadAction<any>) => {
      state.user.aiAssistantPermission = action.payload;
    },
    allowRefferedCandidates: (state, action: PayloadAction<any>) => {
      state.user.allowRefferedCandidates = action.payload;
    },

    departmentCreatePermission: (state, action: PayloadAction<any>) => {
      state.user.departmentCreatePermission = action.payload;
    },
    loggedUser: (state, action: PayloadAction<any>) => {
      state.user.loggedUser = action.payload;
    },
    flowStep: (state, action: PayloadAction<any>) => {
      state.user.flowStep = action.payload;
    },
    addCalendlyData: (state, action: PayloadAction<any>) => {
      state.user.calendlyData = action.payload;
    },
    addRequestScoreActionData: (state, action: PayloadAction<any>) => {
      state.user.scoreActionData = action.payload;
    },
    addBot: (state, action: PayloadAction<any>) => {
      state.user.addBot = action.payload;
    },

    updateProfilePhoto: (state, action: PayloadAction<string>) => {
      state.user.loggedUser.profile_photo = action.payload;
    },
    updateCompanyProfileImage: (state, action: PayloadAction<string>) => {
      if (state.user.company) {
        state.user.company.profileImage = action.payload;
      } else {
        state.user.company = { profileImage: action.payload };
      }
    },
    updateAuthToken: (state, action: PayloadAction<string>) => {
      state.user.token = action.payload;
    },
    updateFcmToken: (state, action: PayloadAction<string>) => {
      state.user.fcmToken = action.payload;
    },
    resetUser: (state) => {
      // Reset user state to initial state (for logout/account deletion)
      state.user = { ...initialState.user, authLifecycle: 'UNAUTHENTICATED' };
    },

    // ─── Legacy Shims (DEPRECATED — will be removed after full migration) ──

    /**
     * @deprecated Use setAuthLifecycle('AUTHENTICATED') or setAuthenticatedUser instead.
     * This shim maps the old boolean to the new lifecycle enum.
     */
    updateSessionStatus: (state, action: PayloadAction<boolean>) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          '[userSlice] DEPRECATED: updateSessionStatus() called. ' +
          'Migrate to setAuthLifecycle() or setAuthenticatedUser().',
          new Error().stack,
        );
      }
      // Map boolean → lifecycle for backward compat
      if (action.payload) {
        state.user.authLifecycle = 'AUTHENTICATED';
      } else {
        state.user.authLifecycle = 'UNAUTHENTICATED';
      }
    },

    /**
     * @deprecated Use setAuthLifecycle('RESTORING') instead.
     * This shim maps the old boolean to the new lifecycle enum.
     */
    updateIsSessionRestoring: (state, action: PayloadAction<boolean>) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          '[userSlice] DEPRECATED: updateIsSessionRestoring() called. ' +
          'Migrate to setAuthLifecycle().',
          new Error().stack,
        );
      }
      if (action.payload) {
        state.user.authLifecycle = 'RESTORING';
      }
      // false case: lifecycle is set by the success/failure path, not here
    },
  },
});

export const {
  // New lifecycle API
  setAuthLifecycle,
  setAuthenticatedUser,
  clearAuthenticatedUser,
  // Existing (non-auth) reducers
  addUser,
  addCompany,
  updateAuthStatus,
  userPermission,
  departmentPermission,
  sceneSetupPermission,
  aiAssistantPermission,
  allowRefferedCandidates,
  departmentCreatePermission,
  loggedUser,
  flowStep,
  addCalendlyData,
  addRequestScoreActionData,
  addBot,
  updateProfilePhoto,
  updateCompanyProfileImage,
  updateAuthToken,
  updateFcmToken,
  resetUser,
  // Legacy shims (DEPRECATED)
  updateSessionStatus,
  updateIsSessionRestoring,
} = userSlice.actions;

export default userSlice.reducer;
